import { useRef, useState, useEffect, useCallback } from 'react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { createUseStyles } from 'react-jss'
import Meta from '@/components/Meta'
import Hero from '@/components/Hero'
import ScrollingStroke from '@/components/ScrollingStroke'
import HeaderLanding from '@/components/HeaderLanding'
import PageElements from '@/components/PageElements'
import Chi from '@/components/Chi'
import WpContactForm7 from '@/components/WpContactForm7'
import InViewTrigger from '@/components/InViewTrigger'
import RevealFadeIn from '@/components/RevealFadeIn'
import RevealText from '@/components/RevealText'
import FooterLanding from '@/components/FooterLanding'
import usePagePrecache from '@/hooks/usePagePrecache'
import useResizeObserver from '@/hooks/useResizeObserver'
import * as contentActions from '@/actions/content'
import { getSlug } from '@/utils/path'
import * as formActions from '@/actions/form'
import * as layoutActions from '@/actions/layout'
import style from './style'

const useStyles = createUseStyles(style)

const Landing = () => {
  const classes = useStyles()
  const $root = useRef()
  const location = useLocation()
  const stub = useRef(getSlug(location.pathname))
  const [isDataFetched, setDataFetched] = useState(false)
  const formId = useRef()

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { page, isPageAnimationReady, currentForm, strings } = useSelector((state) => ({
    page: state.content.pages[stub.current] || {},
    currentForm: state.form[formId.current] || [],
    strings: state.options.strings || {},
    isPageAnimationReady: state.loading.isPageAnimationReady,
    isLoaderExited: state.loading.isLoaderExited,
  }), shallowEqual)

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const fetchPage = useCallback((slug) => dispatch(contentActions.fetchPage(slug)), [dispatch])
  const fetchForm = useCallback((id) => dispatch(formActions.fetchForm(id)), [dispatch])
  const sendContactForm = useCallback((id, data, isMultipart) => dispatch(formActions.sendContactForm(id, data, isMultipart)), [dispatch])
  const setIsLanding = useCallback((value) => dispatch(layoutActions.setLayoutIsLanding(value)), [dispatch])

  /*------------------------------
  Did Mount (Fetch Data)
  ------------------------------*/
  useEffect(() => {
    setIsLanding(true)
    if (Object.keys(page).length === 0) {
      fetchPage(stub.current)
    }
    return () => {
      setIsLanding(false)
    }
  }, [])

  /*------------------------------
  If Page exist call API form
  ------------------------------*/
  useEffect(() => {
    if (Object.keys(page).length > 0) {
      formId.current = page.acf.form.id
      if (formId.current) fetchForm(formId.current)
    }
  }, [page])

  /*------------------------------
  Check data Fetched
  ------------------------------*/
  useEffect(() => {
    if (Object.keys(page).length > 0) setDataFetched(true)
  }, [page])

  /*------------------------------
  Preload
  ------------------------------*/
  const [load] = usePagePrecache({
    init: isDataFetched,
    sources: [],
    callback: () => {},
  })

  /*------------------------------
  Render Helmet
  ------------------------------*/
  const renderHelmet = () => load && <Meta meta={page.yoast_head} />

  /*------------------------------
  Render Hero
  ------------------------------*/
  const renderHero = useCallback(() => {
    return page.content.rendered && (
      <Hero
        text={page.content.rendered}
        visible={isPageAnimationReady}
      />
    )
  }, [page, isPageAnimationReady])

  /*------------------------------
  Render Chi
  ------------------------------*/
  const renderChi = useCallback(() => {
    return (page.acf.chi?.title && page.acf.chi?.text && page.acf.chi?.list) && (
      <Chi
        className={classes.containerForm}
        title={page.acf.chi.title}
        text={page.acf.chi.text}
        list={page.acf.chi.list}
      />
    )
  }, [page])

  /*------------------------------
  Render Form
  ------------------------------*/
  const renderForm = useCallback(() => {
    return currentForm.fields && (
      <InViewTrigger className={classes.containerForm}>
        <div id={page.acf.form.anchor_form.replace('#', '')} className={classes.anchor} />
        {page.acf.form.title ? (
          <RevealText
            tag="h2"
            type="lines"
            value={page.acf.form.title.replace('<p>', '').replace('</p>', '')}
            className={classes.titleForm}
            lineHeight={1.1}
          />
        ) : <div />}
        <RevealFadeIn
          className={classes.form}
        >
          <WpContactForm7
            fields={currentForm.fields}
            formID={formId.current}
            sendContactForm={sendContactForm}
            submitLabel={strings.label_submit}
            onSuccessSend={() => {
              if (page.acf.form.url_thankyou) {
                window.location.href = page.acf.form.url_thankyou
              }
            }}
          />
        </RevealFadeIn>
      </InViewTrigger>
    )
  }, [page, currentForm])

  /*------------------------------
  Render Page Elements
  ------------------------------*/
  const renderPageElements = useCallback(() => {
    return page.acf.page_builder && (
      <PageElements elements={page.acf.page_builder} />
    )
  }, [page])

  useResizeObserver({
    ref: $root.current,
    callback: () => {
      window.dispatchEvent(new Event('resize'))
    },
  })

  return (
    <>
      <HeaderLanding
        mainMenu={page.acf?.main_menu?.list_main}
        headerMenu={page.acf?.header_menu?.list_header}
      />
      <>
        {renderHelmet()}
        {load && (
          <div className={classes.root} ref={$root}>
            <div className={classes.page}>
              <ScrollingStroke />
              <div className={classes.wrapper}>
                {renderHero()}
                {renderPageElements()}
                {renderChi()}
                {renderForm()}
                <FooterLanding className={classes.footer} />
              </div>
            </div>
          </div>
        )}
      </>
    </>
  )
}

export default Landing
